/* Pure promotion rules shared by the storefront controller and regression tests. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PMGiftCore = api;
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const SOURCE = 'pm-gwp';
  const isGift = (item, config) => Boolean(item.properties?._gift_campaign) || confirmed(item, config);
  const isManaged = item => item.properties?._gift_source === SOURCE;
  const slotOf = item => Number(item.properties?._gift_slot);
  const variantOf = item => String(item.variant_id || item.id);
  const normalizedSku = value => String(value || '').trim().toLowerCase();
  function isBuyX(item, config) {
    if (isGift(item, config)) return false;
    const sku = normalizedSku(item.sku);
    // Get Y membership is independent of stock, market visibility and current price.
    if (inPool(item, config) || (sku && config?.giftSkus?.includes(sku))) return false;
    if (config?.buyXMode === 'sku_allowlist') return Boolean(sku && config.buyXSkus?.includes(sku));
    return true;
  }
  function paidSubtotal(cart, config) {
    return cart.items.filter(item => isBuyX(item, config)).reduce((sum, item) => {
      if (!Number.isSafeInteger(item.original_line_price) || item.original_line_price < 0) {
        throw new Error('Missing original cart prices');
      }
      return sum + item.original_line_price;
    }, 0);
  }
  function resolveRule(config, currency) {
    if (String(currency).toUpperCase() !== String(config.currency).toUpperCase()) return null;
    const country = String(config.country || '').toLowerCase();
    const market = String(config.market || '').toLowerCase();
    const matches = String(config.rules || '').split(/\r?\n/).map(line => {
      const [region, code, amount, ...extra] = line.trim().split(':').map(v => v.trim());
      if (extra.length || !region || !code || !/^\d+(?:\.\d{1,2})?$/.test(amount || '')) return null;
      const threshold = Math.round(Number(amount) * 100);
      if (!Number.isSafeInteger(threshold) || threshold <= 0 || code.toUpperCase() !== currency.toUpperCase()) return null;
      const priority = region.toLowerCase() === country ? 2 : region.toLowerCase() === market ? 1 : 0;
      return priority ? { priority, threshold, currency, key: [config.market, config.country, currency].join(':') } : null;
    }).filter(Boolean).sort((a, b) => b.priority - a.priority);
    // Ambiguous duplicate rules fail closed instead of choosing an arbitrary threshold.
    if (!matches.length || matches.filter(r => r.priority === matches[0].priority).length !== 1) return null;
    return matches[0];
  }
  function active(config, now = Date.now()) {
    if (!config.enabled || !config.campaign) return false;
    const parse = value => {
      if (!value) return null;
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return NaN;
      return Date.parse(value);
    };
    const start = parse(config.startsAt), end = parse(config.endsAt);
    if ((start !== null && !Number.isFinite(start)) || (end !== null && !Number.isFinite(end))) return false;
    if (start !== null && end !== null && start >= end) return false;
    return (start === null || now >= start) && (end === null || now < end);
  }
  function state(cart, rule, cap = 0, config) {
    const subtotal = paidSubtotal(cart, config);
    if (!rule) return { subtotal, entitled: 0, remainder: 0, ratio: 0, remaining: 0, capped: false };
    // Compare integer minor units: exactly n * threshold still earns only n - 1 gifts.
    const raw = Math.max(0, Math.floor((subtotal - 1) / rule.threshold));
    const entitled = cap > 0 ? Math.min(raw, Math.floor(cap)) : raw;
    const capped = cap > 0 && raw >= cap;
    const remainder = capped ? rule.threshold : subtotal - raw * rule.threshold;
    return { subtotal, entitled, remainder, capped, ratio: capped ? 1 : Math.min(remainder / (rule.threshold + 1), 1), remaining: capped ? 0 : rule.threshold + 1 - remainder };
  }
  function inPool(item, config) {
    const ids = config?.poolIds || config?.candidates?.map(c => c.id) || [];
    return Boolean(config?.enabled) && ids.some(id => String(id) === variantOf(item));
  }
  // Display follows Shopify's current line price, never a discount title/allocation.
  function confirmed(item, config) {
    return inPool(item, config) && item.quantity > 0 && item.final_line_price === 0;
  }
  function managedGift(item, config, rule) {
    return Boolean(rule) && inPool(item, config) && isManaged(item) &&
      item.properties._gift_campaign === config.campaign && item.properties._gift_rule === rule.key &&
      Number.isInteger(slotOf(item)) && slotOf(item) > 0 && item.quantity === 1;
  }
  // An order-level gift credit covers the group, not particular line allocations.
  // Exact title AND exact amount are required. Paid manual pool lines are ambiguous.
  function giftStatus(cart, config, rule) {
    const managed = cart.items.filter(i => managedGift(i, config, rule));
    const pending = managed.filter(i => !confirmed(i, config));
    const title = String(config.discountTitle || '').trim();
    const discounts = (cart.cart_level_discount_applications || []).filter(d => title && d.title === title);
    const amount = discounts.reduce((n, d) => n + (Number.isSafeInteger(d.total_allocated_amount) && d.total_allocated_amount >= 0 ? d.total_allocated_amount : NaN), 0);
    const expected = pending.reduce((n, i) => n + (Number.isSafeInteger(i.final_line_price) && i.final_line_price > 0 ? i.final_line_price : NaN), 0);
    const ambiguous = cart.items.some(i => (inPool(i, config) || config.giftSkus?.includes(normalizedSku(i.sku))) &&
      !managed.includes(i) && !confirmed(i, config));
    const uniqueSlots = new Set(managed.map(slotOf)).size === managed.length;
    const orderCovered = !ambiguous && uniqueSlots && discounts.length === 1 && expected > 0 && amount === expected;
    const gifts = cart.items.filter(i => confirmed(i, config) || (orderCovered && managed.includes(i)));
    const added = cart.items.filter(i => confirmed(i, config) || managed.includes(i));
    return { gifts, giftCount: gifts.reduce((n, i) => n + i.quantity, 0),
      addedCount: added.reduce((n, i) => n + i.quantity, 0),
      awaitingPrice: pending.length > 0 && !orderCovered, orderCovered, amount, expected, ambiguous };
  }
  function presentation(cart) {
    return JSON.stringify([cart.currency, cart.total_price, cart.cart_level_discount_applications, cart.items.map(i =>
      [i.key, i.quantity, i.original_line_price, i.final_line_price, i.discounts, i.line_level_discount_allocations])]);
  }
  function fingerprint(cart, config, rule) {
    return JSON.stringify([config.campaign, rule?.key, rule?.threshold, config.maxGifts,
      config.candidates.map(c => String(c.id)).sort(), config.poolIds, config.giftSkus, config.buyXMode, config.buyXSkus,
      cart.items.filter(i => !isManaged(i)).map(i => [i.key, i.sku, i.quantity, i.original_line_price, i.final_line_price]).sort()]);
  }
  function ledgerKey(cart, config, rule) {
    // Non-security hash: avoid saving Shopify's cart token verbatim in localStorage keys.
    let hash = 2166136261;
    for (const c of String(cart.token || '')) hash = Math.imul(hash ^ c.charCodeAt(0), 16777619);
    return ['pm-gwp-v1', config.campaign, rule?.key || config.market, (hash >>> 0).toString(16)].join(':');
  }
  function randomIndex(length, random = () => {
    const value = new Uint32Array(1); globalThis.crypto.getRandomValues(value); return value[0];
  }) {
    if (!Number.isInteger(length) || length < 1) throw new Error('Empty gift pool');
    const limit = Math.floor(0x100000000 / length) * length;
    let value; do { value = random(); } while (value >= limit);
    return value % length;
  }
  function nextAction(cart, config, rule, ledger, candidates) {
    const summary = state(cart, rule, config.maxGifts, config);
    // A manually added pool line made free by Regios already fills gift slots.
    const external = cart.items.filter(i => !isManaged(i) && confirmed(i, config));
    const externalCount = external.reduce((n, i) => n + i.quantity, 0);
    const managedEntitlement = Math.max(0, summary.entitled - externalCount);
    const seen = new Set();
    for (const item of cart.items.filter(isManaged)) {
      const slot = slotOf(item);
      if (!rule || item.properties._gift_campaign !== config.campaign || item.properties._gift_rule !== rule.key ||
          !Number.isInteger(slot) || slot < 1 || slot > managedEntitlement || ledger.waived[slot] || seen.has(slot) ||
          !(config.poolIds || candidates.map(c => c.id)).some(id => String(id) === variantOf(item))) return { type: 'remove', item };
      seen.add(slot);
      if (item.quantity !== 1) return { type: 'normalize', item };
    }
    if (rule && externalCount > summary.entitled) {
      const item = external[external.length - 1];
      return { type: 'reduce', item, quantity: Math.max(0, item.quantity - (externalCount - summary.entitled)) };
    }
    for (let slot = 1; slot <= managedEntitlement; slot++) {
      if (!seen.has(slot) && !ledger.waived[slot]) return { type: 'add', slot };
    }
    return { type: 'settled' };
  }
  return { SOURCE, isGift, isManaged, slotOf, variantOf, isBuyX, paidSubtotal, resolveRule, active, state, inPool, confirmed, managedGift, giftStatus, presentation, fingerprint, ledgerKey, randomIndex, nextAction };
});
